// src/pages/fleet/FleetHelpdeskPage.tsx — Helpdesk & Support.
//
// The page an operator reaches when something outside this system is in the way:
// a GPS provider's mail is not arriving, a manual is needed, or the application
// itself is misbehaving. Everything here is a contact or a download — there is no
// state to get wrong, which is why it is open to anyone signed in rather than
// gated behind a permission.
//
// The whitelist block exists because RFCL's mail server silently drops mail from
// the GPS vendors, and every integration delay so far has started with a message
// nobody received. The addresses are copyable in one tap so they can be handed
// straight to an IT administrator.
import React, { useState } from 'react';
import { P } from './fleetStyles';
import Icon from './Icon';
import useIsMobile from './useIsMobile';
import { css } from '../../utils/css';

/** Who to whitelist, per GPS provider. */
const SENDERS: { name: string; hint: string; addresses: string[] }[] = [
  { name: 'WheelsEye', hint: 'GPS provider', addresses: ['apisupport@wheelseye.com', 'bhawesh.jha@wheelseye.com'] },
  { name: 'BlackBuck', hint: 'GPS provider', addresses: ['@blackbuck.com'] },
  { name: 'Margo Logistics', hint: 'GPS provider', addresses: ['info@margologistics.in'] },
];

/** Downloads live in frontend/public/manuals so they are served as plain files. */
const MANUALS: { title: string; note: string; file: string }[] = [
  { title: 'User Manual', note: 'How to use the portal — trips, tracking, reports and masters.', file: '/manuals/VLTS_User_Manual.pdf' },
  { title: 'SOP Manual', note: 'The VLTS / IFMS standard operating procedure this system follows.', file: '/manuals/VLTS_IFMS_SOP.pdf' },
];

const IT_EMAIL = 'suraj@makelinkit.com';

export default function FleetHelpdeskPage() {
  const isMobile = useIsMobile();
  const [copied, setCopied] = useState<string | null>(null);

  /** Copy one provider's addresses; the tick is feedback that it actually happened. */
  const copy = async (key: string, text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(key);
      setTimeout(() => setCopied((c) => (c === key ? null : c)), 1800);
    } catch {
      // Clipboard is blocked outside a secure context; the address is on screen
      // to be read anyway, so this is not worth an error message.
    }
  };

  const grid = { display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit,minmax(300px,1fr))', gap: 14 };

  return (
    <div style={P.page}>
      <div style={S.hero}>
        <div>
          <h1 style={S.heroTitle}>Helpdesk &amp; Support</h1>
          <p style={S.heroSub}>Get assistance with whitelist issues, IT support, and user manuals.</p>
        </div>
        <div style={S.heroIcon}><Icon name="headset" size={22} stroke={1.9} /></div>
      </div>

      {/* ── Whitelist ── */}
      <section style={S.card}>
        <div style={S.cardHead}>
          <div style={{ ...S.badge, background: 'var(--fx-green-soft)', color: 'var(--fx-green)' }}>
            <Icon name="shield" size={18} stroke={2} />
          </div>
          <div>
            <h2 style={S.cardTitle}>Whitelist Issue</h2>
            <p style={S.cardSub}>If you are unable to receive emails or access required services, check the following email domains.</p>
          </div>
        </div>

        <div style={S.cardBody}>
          <div style={S.notice}>
            <Icon name="bell" size={14} stroke={2} />
            <span>Ask your IT / Network Administrator to whitelist the addresses below.</span>
          </div>

          <div style={grid}>
            {SENDERS.map((s) => {
              const all = s.addresses.join(', ');
              const done = copied === s.name;
              return (
                <div key={s.name} style={S.sender}>
                  <div style={S.senderIcon}><Icon name="mail" size={16} stroke={1.9} /></div>
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={S.senderName}>{s.name} <span style={S.senderHint}>· {s.hint}</span></div>
                    {s.addresses.map((a) => <div key={a} style={S.address}>{a}</div>)}
                  </div>
                  <button
                    type="button"
                    style={{ ...S.copyBtn, ...(done ? S.copyBtnDone : {}) }}
                    title={done ? 'Copied' : `Copy ${s.name} address${s.addresses.length > 1 ? 'es' : ''}`}
                    onClick={() => copy(s.name, all)}
                  >
                    <Icon name={done ? 'check' : 'copy'} size={14} stroke={2.1} />
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── IT support + manuals ── */}
      <div style={grid}>
        <section style={{ ...S.card, marginBottom: 0 }}>
          <div style={S.tileBody}>
            <div style={{ ...S.badge, background: 'var(--fx-accent-soft)', color: 'var(--fx-accent)' }}>
              <Icon name="lifebuoy" size={18} stroke={1.9} />
            </div>
            <div style={S.kicker}>IT Support</div>
            <h3 style={S.tileTitle}>Need technical assistance?</h3>
            <p style={S.tileNote}>For any technical issue, system access problem, or application-related support, contact our IT team.</p>
            <a href={`mailto:${IT_EMAIL}?subject=Fertilizer%20Tracking%20—%20support%20request`} style={S.mailBtn}>
              <Icon name="mail" size={14} stroke={2} /> {IT_EMAIL}
            </a>
          </div>
        </section>

        {MANUALS.map((m) => (
          <section key={m.title} style={{ ...S.card, marginBottom: 0 }}>
            <div style={S.tileBody}>
              <div style={{ ...S.badge, background: 'var(--fx-purple-soft, rgba(175,82,222,.14))', color: '#AF52DE' }}>
                <Icon name="book" size={18} stroke={1.9} />
              </div>
              <div style={S.kicker}>Documentation</div>
              <h3 style={S.tileTitle}>{m.title}</h3>
              <p style={S.tileNote}>{m.note}</p>
              {/* `download` so the browser saves it instead of opening a viewer tab. */}
              <a href={m.file} download style={{ ...S.mailBtn, ...S.dlBtn }}>
                <Icon name="download" size={14} stroke={2} /> Download {m.title}
              </a>
            </div>
          </section>
        ))}
      </div>
    </div>
  );
}

const S = css({
  hero: {
    background: 'var(--fx-surface)', border: '1px solid var(--fx-border)', borderRadius: 14,
    padding: '18px 20px', marginBottom: 16, display: 'flex', alignItems: 'center',
    justifyContent: 'space-between', gap: 16, boxShadow: 'var(--fx-shadow)',
  },
  heroTitle: { fontSize: 20, fontWeight: 800, color: 'var(--fx-text)', margin: 0, letterSpacing: '-0.01em' },
  heroSub: { fontSize: 13, color: 'var(--fx-text-2)', margin: '4px 0 0' },
  heroIcon: {
    width: 44, height: 44, borderRadius: 12, flexShrink: 0, display: 'flex', alignItems: 'center',
    justifyContent: 'center', background: 'var(--fx-accent-soft)', color: 'var(--fx-accent)',
  },

  card: {
    background: 'var(--fx-surface)', border: '1px solid var(--fx-border)', borderRadius: 14,
    marginBottom: 16, overflow: 'hidden', boxShadow: 'var(--fx-shadow)',
  },
  cardHead: { display: 'flex', gap: 13, alignItems: 'flex-start', padding: '16px 20px', borderBottom: '1px solid var(--fx-border)' },
  cardTitle: { fontSize: 15, fontWeight: 700, color: 'var(--fx-text)', margin: 0 },
  cardSub: { fontSize: 12.5, color: 'var(--fx-text-2)', margin: '3px 0 0', lineHeight: 1.5 },
  cardBody: { padding: '16px 20px 20px' },
  badge: { width: 36, height: 36, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },

  notice: {
    display: 'flex', alignItems: 'center', gap: 9, fontSize: 12.5, color: 'var(--fx-text-2)',
    background: 'var(--fx-gray-soft)', border: '1px solid var(--fx-border)', borderRadius: 10,
    padding: '10px 13px', marginBottom: 14,
  },

  sender: {
    display: 'flex', alignItems: 'flex-start', gap: 11, padding: '13px 14px',
    border: '1px solid var(--fx-border)', borderRadius: 12, background: 'var(--fx-surface-2)',
  },
  senderIcon: {
    width: 32, height: 32, borderRadius: 9, flexShrink: 0, display: 'flex', alignItems: 'center',
    justifyContent: 'center', background: 'var(--fx-surface)', border: '1px solid var(--fx-border)', color: 'var(--fx-text-2)',
  },
  senderName: { fontSize: 11.5, color: 'var(--fx-text-2)', marginBottom: 3 },
  senderHint: { color: 'var(--fx-text-3)' },
  address: { fontSize: 13, fontWeight: 600, color: 'var(--fx-text)', wordBreak: 'break-all', lineHeight: 1.5 },
  copyBtn: {
    width: 30, height: 30, borderRadius: 8, flexShrink: 0, cursor: 'pointer',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    background: 'var(--fx-surface)', border: '1px solid var(--fx-border-strong)',
    color: 'var(--fx-text-2)', fontFamily: 'inherit', transition: 'color .15s, border-color .15s',
  },
  copyBtnDone: { color: 'var(--fx-green)', borderColor: 'var(--fx-green)' },

  tileBody: { padding: '18px 20px 20px' },
  kicker: { fontSize: 10.5, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--fx-text-3)', margin: '13px 0 5px' },
  tileTitle: { fontSize: 15.5, fontWeight: 700, color: 'var(--fx-text)', margin: 0 },
  tileNote: { fontSize: 12.5, color: 'var(--fx-text-2)', lineHeight: 1.6, margin: '7px 0 15px' },
  mailBtn: {
    display: 'inline-flex', alignItems: 'center', gap: 7, padding: '9px 14px', borderRadius: 10,
    background: 'var(--fx-accent-soft)', color: 'var(--fx-accent)', fontSize: 12.5, fontWeight: 600,
    textDecoration: 'none', border: '1px solid transparent',
  },
  dlBtn: { background: 'rgba(175,82,222,.12)', color: '#AF52DE' },
});
